// Navigation model for the change navigator (the shared source for "passage 2
// of 7"). It groups a result's `changes[]` into STOPS — the coherent passages,
// blocks and records a reader actually moves between — and it does so without
// touching any count.
//
// The distinction this module exists to protect: a stat card's number is a
// SEMANTIC TOTAL ("95 words added"); a stop count is HOW MANY PLACES YOU CAN
// STAND ("7 passages"). Those 95 words may live in 7 passages, and a navigator
// that claimed 95 stops would be lying. Both numbers are therefore reported,
// in their own units, from one model:
//
//     95 words added · passage 2 of 7
//
// `category.total` is read VERBATIM off the result — never recomputed from the
// stops — so grouping can change without moving a published count.
//
// Pure, like change-analysis.js: a deterministic reading of an existing result.
// It adds no field to the result and mutates nothing. Locations are described
// in the result's own coordinates (span index, line number, record key, path);
// turning one into a DOM target is the renderer's job, not this module's.

// Tunables, named so the grouping rule is one edit rather than a hunt.
export const STOP_GROUPING = {
  // Prose: two edits separated by this little untouched text read as one edit.
  // `2026-05-12` → `2026-06-09` arrives as two runs (`05→06`, `12→09`) on one
  // line; a reader sees one change and must not be asked to press Next twice.
  // A newline always ends a passage, however short the gap.
  proseGapWords: 8,
  // Text: consecutive changed lines are one block. A gap of 1 means "the very
  // next line"; a single untouched line between two edits starts a new block.
  textGapLines: 1,
};

// change.kind → navigator category. The ids are the join with the stat cards
// (summary.js stamps the same id on each card), which is why they are explicit
// rather than derived from the card's CSS class: `typeChanges` and `modified`
// share the class `changed`, so the class is not a key.
const CATEGORY_OF_KIND = {
  add: 'added',
  'record-add': 'added',
  'column-add': 'added',
  remove: 'removed',
  'record-remove': 'removed',
  'column-remove': 'removed',
  change: 'changed',
  'record-modify': 'changed',
  type: 'typeChanges',
  'column-type': 'typeChanges',
  move: 'moved',
};

// Changes that describe the comparison rather than a place in it. They are not
// stops — a navigator must never move somewhere it cannot show — but they are
// listed as `notes` so every change index is still accounted for.
const NOTE_KINDS = new Set(['reorder', 'keywarn', 'invalid-key']);

const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);

// Which cards a single change feeds. Normally one — but prose counts a
// replacement into ALL THREE of its counters (diffProse adds every `old` span's
// words to `removed`, every `new` span's to `added`, and the pair to
// `modified`), so a replacement passage has to be reachable from all three or
// "91 words removed" would be a card you can click with nothing to visit.
// Every other engine counts a modification once, and this stays a single entry.
function categoriesOfChange(renderKind, change) {
  const cat = CATEGORY_OF_KIND[change?.kind];
  if (!cat) return [];
  if (renderKind === 'prose' && change.kind === 'change') return ['changed', 'removed', 'added'];
  return [cat];
}

// What one stop is called, per format. Used by the "· passage 2 of 7" half of
// the line, never by the count half.
const STOP_NOUN = {
  prose: 'passage',
  text: 'block',
  records: 'record',
  structural: 'change',
};

export function changeStops(result) {
  const kind = result?.render?.kind || 'text';
  const changes = Array.isArray(result?.changes) ? result.changes : [];
  const stopNoun = STOP_NOUN[kind] || 'change';

  const notes = [];
  const navigable = [];
  changes.forEach((c, i) => {
    if (NOTE_KINDS.has(c.kind)) notes.push(i);
    else navigable.push(i);
  });

  // Built ONCE per model: every stop's location and context look their runs up
  // here. Rebuilding it per stop made a long document with many separated edits
  // quadratic (2,000 edits: 141 ms → see REL-089).
  const prose = kind === 'prose' ? proseIndex(result, navigable) : null;

  let groups;
  if (kind === 'prose') groups = groupProse(result, navigable, prose.units);
  else if (kind === 'text') groups = groupText(result, navigable);
  else groups = navigable.map((i) => [i]);

  const stops = groups.map((changeIndexes, index) => {
    const cats = [];
    for (const ci of changeIndexes) {
      // A merged passage can hold a removal and an addition at once, so a stop
      // belongs to EVERY category it contains. "words added · passage 2 of 7"
      // then means the 2nd of the 7 passages that contain added words — true
      // whatever else those passages also contain.
      for (const cat of categoriesOfChange(kind, changes[ci])) {
        if (!cats.includes(cat)) cats.push(cat);
      }
    }
    return {
      id: `st-${changeIndexes[0]}`, // derived from content position, so the same result always yields the same ids
      index,
      categories: cats,
      kinds: changeIndexes.map((ci) => changes[ci]?.kind).filter(Boolean),
      changeIndexes,
      location: locationFor(result, kind, changeIndexes, prose),
      // What the reader needs to recognise the place BEFORE jumping to it. A
      // prefix alone does not rescue `05 → 06`; the surrounding words do.
      ...describe(result, kind, changeIndexes, prose),
    };
  });

  return {
    format: result?.format,
    renderKind: kind,
    stopNoun,
    stops,
    notes,
    categories: categoriesFor(result, kind, stops),
  };
}

// Counts are READ, never derived: `total` is the same number the stat card
// shows, straight off the result. `stopIndexes` is the navigation half.
function categoriesFor(result, kind, stops) {
  const stopsOf = (id) => stops.filter((s) => s.categories.includes(id)).map((s) => s.index);
  const cat = (id, total, unit) => ({
    id,
    total: total || 0,
    unit,
    stopIndexes: stopsOf(id),
    // `moved` on a record set is the "order changed" footnote (spec §8: one
    // footnote, never per-row highlights), so it has a total and no stops.
    get navigable() { return this.stopIndexes.length > 0; },
  });
  const out = [];
  if (kind === 'prose') {
    out.push(cat('changed', result.modified, 'replacement'));
    out.push(cat('added', result.added, 'word'));
    out.push(cat('removed', result.removed, 'word'));
  } else if (kind === 'records') {
    if (result.typeChanges) out.push(cat('typeChanges', result.typeChanges, 'schema change'));
    out.push(cat('added', result.added, 'record'));
    out.push(cat('removed', result.removed, 'record'));
    if (result.modified) out.push(cat('changed', result.modified, 'record'));
    if (result.moved) out.push(cat('moved', result.moved, 'order change'));
  } else if (kind === 'structural') {
    if (result.typeChanges) out.push(cat('typeChanges', result.typeChanges, 'type change'));
    if (result.modified) out.push(cat('changed', result.modified, 'value'));
    out.push(cat('added', result.added, 'path'));
    out.push(cat('removed', result.removed, 'path'));
  } else {
    if (result.moved) out.push(cat('moved', result.moved, 'block'));
    if (result.modified) out.push(cat('changed', result.modified, 'line'));
    out.push(cat('added', result.added, 'line'));
    out.push(cat('removed', result.removed, 'line'));
  }
  return out;
}

/* ---------- prose: passages ---------- */

// Re-walks `render.spans` with diffProse's own pairing rule to learn which spans
// each change came from — information the change objects don't carry. The walk
// is asserted against `changes.length` in tests, and degrades to one stop per
// change at runtime if it ever drifts, so navigation can be wrong-sized but
// never wrong.
function proseUnits(result) {
  const spans = result.render?.spans || [];
  const units = [];
  for (let i = 0; i < spans.length; i++) {
    const cur = spans[i];
    const next = spans[i + 1];
    if (cur.type === 'old' && next && next.type === 'new') {
      units.push({ spanStart: i, spanEnd: i + 1 });
      i++;
    } else if (cur.type === 'old' && cur.text.trim()) {
      units.push({ spanStart: i, spanEnd: i });
    } else if (cur.type === 'new' && cur.text.trim()) {
      units.push({ spanStart: i, spanEnd: i });
    }
  }
  return units;
}

// The runs of each navigable change, by change index. Empty when the walk drifted
// from the change list, so every lookup degrades exactly as groupProse does.
function proseIndex(result, navigable) {
  const units = proseUnits(result);
  const unitOf = new Map();
  if (units.length === navigable.length) navigable.forEach((ci, k) => unitOf.set(ci, units[k]));
  return { units, unitOf };
}

function groupProse(result, navigable, units) {
  const spans = result.render?.spans || [];
  if (units.length !== navigable.length) return navigable.map((i) => [i]); // drifted — degrade, don't guess
  const groups = [];
  let cur = null;
  units.forEach((u, k) => {
    if (cur) {
      const gap = spans.slice(cur.spanEnd + 1, u.spanStart).map((s) => s.text).join('');
      // A newline ends a passage however short the gap: separate bullets and
      // separate paragraphs are separate places, even when adjacent on screen.
      const joins = !/\n/.test(gap) && wordCount(gap) <= STOP_GROUPING.proseGapWords;
      if (joins) {
        cur.changeIndexes.push(navigable[k]);
        cur.spanEnd = u.spanEnd;
        return;
      }
    }
    cur = { changeIndexes: [navigable[k]], spanStart: u.spanStart, spanEnd: u.spanEnd };
    groups.push(cur);
  });
  return groups.map((g) => g.changeIndexes);
}

/* ---------- text: contiguous blocks ---------- */

// Lines carried by each change kind (interior edits of a moved block have only
// a right-hand line, which is why each side is optional).
function textLines(c) {
  if (c.kind === 'move') return { l: c.fromStart, r: c.toStart, lEnd: c.fromEnd, rEnd: c.toEnd };
  return { l: c.lineL, r: c.lineR, lEnd: c.lineL, rEnd: c.lineR };
}

function groupText(result, navigable) {
  const changes = result.changes || [];
  const groups = [];
  let cur = null;
  let last = null;
  for (const ci of navigable) {
    const c = changes[ci];
    const pos = textLines(c);
    // A move is its own stop: it has two ends, and merging it into a
    // neighbouring block would give the stop two unrelated locations.
    const isMove = c.kind === 'move';
    const wasMove = last && last.kind === 'move';
    let joins = false;
    if (cur && !isMove && !wasMove) {
      const gapR = pos.r != null && last.rEnd != null ? pos.r - last.rEnd : null;
      const gapL = pos.l != null && last.lEnd != null ? pos.l - last.lEnd : null;
      // Adjacent on a side the two changes share. A pure removal and a pure
      // addition share no side, so they stay separate stops.
      joins = (gapR != null && gapR >= 0 && gapR <= STOP_GROUPING.textGapLines)
        || (gapL != null && gapL >= 0 && gapL <= STOP_GROUPING.textGapLines);
    }
    if (joins) {
      cur.push(ci);
      last = {
        kind: c.kind,
        lEnd: pos.lEnd ?? last.lEnd,
        rEnd: pos.rEnd ?? last.rEnd,
      };
    } else {
      cur = [ci];
      groups.push(cur);
      last = { kind: c.kind, lEnd: pos.lEnd, rEnd: pos.rEnd };
    }
  }
  return groups;
}

/* ---------- locations ---------- */

// Described in the RESULT's coordinates. Resolving one to a DOM node — including
// scrolling a virtualized window or paging a record table to reveal it — belongs
// to the renderer that owns those coordinates.
function locationFor(result, kind, changeIndexes, prose) {
  const changes = result.changes || [];
  const first = changes[changeIndexes[0]];
  const last = changes[changeIndexes[changeIndexes.length - 1]];
  if (!first) return null;

  if (kind === 'prose') {
    const startU = prose.unitOf.get(changeIndexes[0]);
    const endU = prose.unitOf.get(changeIndexes[changeIndexes.length - 1]);
    if (!startU || !endU) return { kind: 'span' };
    return { kind: 'span', spanStart: startU.spanStart, spanEnd: endU.spanEnd };
  }

  if (kind === 'text') {
    if (first.kind === 'move') {
      return { kind: 'move', fromStart: first.fromStart, fromEnd: first.fromEnd, toStart: first.toStart, toEnd: first.toEnd };
    }
    const a = textLines(first);
    const b = textLines(last);
    return {
      kind: 'line',
      lineL: a.l ?? null,
      lineR: a.r ?? null,
      lineLEnd: b.lEnd ?? a.lEnd ?? null,
      lineREnd: b.rEnd ?? a.rEnd ?? null,
    };
  }

  if (first.kind === 'column-add' || first.kind === 'column-remove' || first.kind === 'column-type') {
    return { kind: 'column', name: first.name };
  }
  // A JSON field change carries BOTH a recordKey (the array element it came
  // from) and a path; the ledger it has to be found in decides which one is the
  // address. Structural results are addressed by path, record sets by key.
  const byPath = () => (first.path === undefined
    ? null
    : { kind: 'path', path: first.path, rowIndex: rowIndexFor(result, 'path', first.path) });
  const byRecord = () => (first.recordKey === undefined
    ? null
    : { kind: 'record', recordKey: first.recordKey, rowIndex: rowIndexFor(result, 'key', first.recordKey) });
  const located = kind === 'structural' ? (byPath() || byRecord()) : (byRecord() || byPath());
  if (located) return located;
  return null;
}

// One index per result, built lazily and cached on a WeakMap so a 500k-row
// dataset doesn't get an O(stops x rows) scan.
const ROW_INDEX = new WeakMap();
function rowIndexFor(result, field, value) {
  const rows = result?.render?.rows;
  if (!Array.isArray(rows)) return null;
  let maps = ROW_INDEX.get(result);
  if (!maps) {
    maps = {};
    ROW_INDEX.set(result, maps);
  }
  if (!maps[field]) {
    const m = new Map();
    rows.forEach((r, i) => {
      if (r && r[field] !== undefined && !m.has(r[field])) m.set(r[field], i);
    });
    maps[field] = m;
  }
  const idx = maps[field].get(value);
  return idx === undefined ? null : idx;
}

/* ---------- readable context ---------- */

// How much untouched text to keep on each side of an edit. Enough to recognise
// the line, short enough that six findings still fit above the diff.
const CONTEXT_CHARS = 30;

// `label` is WHERE (line 8, lines 7-10, a record key, a leaf path) and
// `context` is WHAT IT READS LIKE — typed segments, never HTML. The renderer
// escapes them; this module stays pure and DOM-free, the same contract
// change-analysis.js keeps with its raw field names.
function describe(result, kind, changeIndexes, prose) {
  const changes = result.changes || [];
  const first = changes[changeIndexes[0]];
  if (!first) return {};

  if (kind === 'prose') return { label: null, context: proseContext(result, changeIndexes, prose) };

  if (kind === 'text') {
    if (first.kind === 'move') return { label: null, context: null }; // the move finding already states both ranges
    const lines = changeIndexes.map((ci) => changes[ci]).map(textLines);
    const lo = lines[0];
    const hi = lines[lines.length - 1];
    const startL = lo.l ?? null;
    const startR = lo.r ?? null;
    const endL = hi.lEnd ?? startL;
    const endR = hi.rEnd ?? startR;
    // Name the side the reader will actually look at: the changed document
    // where there is one, the original for a pure deletion.
    const from = startR ?? startL;
    const to = endR ?? endL;
    const label = from == null ? null : from === to ? `line ${from}` : `lines ${from}-${to}`;
    return { label, context: trimContext(textSegments(first)) };
  }

  if (first.kind === 'column-add' || first.kind === 'column-remove' || first.kind === 'column-type') {
    return { label: first.name, context: null };
  }
  if (first.recordKey !== undefined && kind === 'records') return { label: String(first.recordKey), context: null };
  if (first.path !== undefined) return { label: String(first.path), context: null };
  return {};
}

function textSegments(change) {
  if (change.inline) {
    return change.inline.map((sp) => ({ type: sp.type, text: sp.text }));
  }
  if (change.kind === 'add') return [{ type: 'new', text: change.text ?? '' }];
  if (change.kind === 'remove') return [{ type: 'old', text: change.text ?? '' }];
  return null;
}

function proseContext(result, changeIndexes, prose) {
  const spans = result.render?.spans || [];
  const startU = prose.unitOf.get(changeIndexes[0]);
  const endU = prose.unitOf.get(changeIndexes[changeIndexes.length - 1]);
  if (!startU || !endU) return null;
  // One span of untouched text on each side, plus everything between the
  // stop's own runs — so a merged passage reads as the sentence it is.
  const before = spans[startU.spanStart - 1];
  const after = spans[endU.spanEnd + 1];
  const segs = [];
  if (before && before.type === 'ctx') segs.push({ type: 'ctx', text: before.text });
  for (let i = startU.spanStart; i <= endU.spanEnd; i++) {
    const sp = spans[i];
    if (sp) segs.push({ type: sp.type, text: sp.text });
  }
  if (after && after.type === 'ctx') segs.push({ type: 'ctx', text: after.text });
  return trimContext(segs);
}

// Keep the edits whole and trim the untouched ends, marking where text was cut.
// The changed words are the finding; context is only there to place them.
function trimContext(segs) {
  if (!segs || !segs.length) return null;
  const out = segs.map((s) => ({ ...s, text: String(s.text).replace(/\s*\n\s*/g, ' ') }));
  const firstEdit = out.findIndex((s) => s.type !== 'ctx');
  const lastEdit = out.length - 1 - [...out].reverse().findIndex((s) => s.type !== 'ctx');
  if (firstEdit < 0) return null;
  for (let i = 0; i < firstEdit; i++) out[i] = { ...out[i], text: '' };
  for (let i = lastEdit + 1; i < out.length; i++) out[i] = { ...out[i], text: '' };
  const lead = out[firstEdit - 1];
  if (lead && lead.text === '' && segs[firstEdit - 1]) {
    const t = String(segs[firstEdit - 1].text).replace(/\s*\n\s*/g, ' ');
    lead.text = t.length > CONTEXT_CHARS ? `…${t.slice(t.length - CONTEXT_CHARS)}` : t;
  }
  const tail = out[lastEdit + 1];
  if (tail && tail.text === '' && segs[lastEdit + 1]) {
    const t = String(segs[lastEdit + 1].text).replace(/\s*\n\s*/g, ' ');
    tail.text = t.length > CONTEXT_CHARS ? `${t.slice(0, CONTEXT_CHARS)}…` : t;
  }
  return out.filter((s) => s.text !== '');
}
